import React, { useState } from "react";
import { Twitter, Facebook, Linkedin, MessageCircle, Link2, Check, Instagram } from "lucide-react";
import { toast } from "sonner";

const IconBtn = ({ onClick, label, testid, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    data-testid={testid}
    className="inline-flex h-9 w-9 items-center justify-center border border-line text-faint transition-colors hover:border-gold/60 hover:text-gold"
  >
    {children}
  </button>
);

// Copy text with a fallback for sandboxed / insecure contexts where
// navigator.clipboard is blocked (e.g. the preview iframe).
async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export const ShareBar = ({ title, summary }) => {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = summary ? `${title} — ${summary}` : title;
  const eu = encodeURIComponent(url);
  const et = encodeURIComponent(text);

  // Try to open a share window; if the popup is blocked (sandboxed preview),
  // fall back to copying the destination so the action is never a dead click.
  const openShare = async (shareUrl, network) => {
    const win = window.open(shareUrl, "_blank", "noopener,noreferrer");
    if (!win || win.closed || typeof win.closed === "undefined") {
      const ok = await copyText(shareUrl);
      toast.message(`${network} share link ${ok ? "copied" : "ready"}`, {
        description: ok
          ? "Popups are blocked here (preview). Paste the link to share — it opens normally on the live site."
          : shareUrl,
        duration: 6000,
      });
    }
  };

  const copyLink = async () => {
    const ok = await copyText(url);
    if (ok) {
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.message("Copy the link", { description: url, duration: 6000 });
    }
  };

  const shareInstagram = async () => {
    const caption = `${text}\n\n${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
        return;
      }
    } catch {
      return; // user dismissed the native sheet
    }
    const ok = await copyText(caption);
    toast.message(ok ? "Caption + link copied" : "Copy for Instagram", {
      description: ok ? "Paste it into your Instagram story or bio." : caption,
      duration: 6000,
    });
  };

  const targets = [
    { network: "X", testid: "share-x", icon: Twitter,
      url: `https://twitter.com/intent/tweet?url=${eu}&text=${et}` },
    { network: "Facebook", testid: "share-facebook", icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${eu}` },
    { network: "LinkedIn", testid: "share-linkedin", icon: Linkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${eu}` },
    { network: "WhatsApp", testid: "share-whatsapp", icon: MessageCircle,
      url: `https://api.whatsapp.com/send?text=${et}%20${eu}` },
  ];

  return (
    <div className="flex items-center gap-3" data-testid="share-bar">
      <span className="eyebrow text-faint">Share</span>
      <div className="flex items-center gap-2">
        {targets.map(({ network, testid, icon: Icon, url: shareUrl }) => (
          <IconBtn key={testid} label={`Share on ${network}`} testid={testid}
            onClick={() => openShare(shareUrl, network)}>
            <Icon size={15} />
          </IconBtn>
        ))}
        <IconBtn label="Share on Instagram" testid="share-instagram" onClick={shareInstagram}>
          <Instagram size={15} />
        </IconBtn>
        <IconBtn label="Copy link" testid="share-copy" onClick={copyLink}>
          {copied ? <Check size={15} className="text-gold" /> : <Link2 size={15} />}
        </IconBtn>
      </div>
    </div>
  );
};

export default ShareBar;
